/**
 * useTaller - Hook personalizado para gestión de talleres
 *
 * Centraliza la lógica común de talleres:
 * - Fetching de datos desde Firestore
 * - Cálculo de estados
 * - Validaciones de cupo y permisos
 * - Operaciones de inscripción
 */

import { useState, useCallback } from 'react';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  updateDoc,
  deleteDoc,
  runTransaction,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { v4 as uuidv4 } from 'uuid';
import { calcularEstadoTaller } from '../utils/tallerEstadoHelper';

// ============ INTERFACES ============

export interface Taller {
  id?: string;
  taller_id?: string;
  nombre: string;
  descripcion?: string;
  descripcionGeneral?: string;
  objetivo?: string;
  fecha?: any;
  fechaInicio?: any;
  fechaTermino?: any;
  horaInicio?: string;
  horaTermino?: string;
  horario?: string;
  lugar?: string;
  direccion?: string;
  ciudad?: string;
  organizadorPrincipal?: string;
  expositor?: string;
  responsable?: {
    nombre: string;
    cargo: string;
    correo?: string;
    telefono?: string;
  };
  cupoMaximo?: number;
  cupo?: string;
  tipoCosto?: 'gratuito' | 'pago';
  precio?: number;
  moneda?: string;
  creditos?: number;
  estado?: 'proximo' | 'en_curso' | 'finalizado' | 'cancelado';
  estadoCalculado?: string;
  publicoObjetivo?: 'afiliados' | 'asociados' | 'afiliados_asociados' | 'abierto_publico' | string;
  inscritos?: number;
  bannerUrl?: string;
  tipoEvento?: 'presencial' | 'virtual' | 'hibrido';
  requiereRegistro?: boolean;
  certifica?: boolean;
  certificacion?: string;
  activo?: boolean;
  actividades?: string[];
  ponentes?: any[];
  agenda?: any[];
  patrocinadores?: any[];
  datosPago?: {
    titular?: string;
    banco?: string;
    cuenta?: string;
    clabe?: string;
    referencia?: string;
    instrucciones?: string;
  };
}

export interface Inscripcion {
  id?: string;
  perfil_id: string;
  tipo_perfil: string;
  taller_doc_id: string;
  asesor_doc_id?: string;
  asociado_doc_id?: string;
  nombre?: string;
  correo?: string;
  telefono?: string;
  codigo_qr?: string;
  fechaInscripcion?: any;
  fecha_inscripcion?: any;
  asistencia?: boolean;
  completado?: boolean;
  estatus_pago?: string;
  creditos?: number;
  tipo?: 'afiliado' | 'asociado' | 'externo';
  comprobantePagoUrl?: string;
  comprobanteNombre?: string;
  comprobanteFecha?: any;
}

export interface InscripcionExterna {
  id?: string;
  taller_doc_id: string;
  nombre: string;
  correo: string;
  telefono?: string;
  inmobiliaria?: string;
  codigo_qr?: string;
  fechaInscripcion?: any;
  fecha_inscripcion?: any;
  asistencia?: boolean;
  completado?: boolean;
  estatus_pago?: string;
  comprobantePagoUrl?: string;
  comprobanteNombre?: string;
  comprobanteFecha?: any;
}

export interface UserPerfil {
  id: string;
  tipo: 'afiliado' | 'asociado';
  nombre?: string;
  correo?: string;
}

export interface TallerFilters {
  nombre?: string;
  mes?: string;
  costo?: 'todos' | 'gratuito' | 'pago';
  creditos?: 'todos' | 'con_creditos' | 'sin_creditos';
  estado?: 'todos' | 'proximo' | 'en_curso' | 'finalizado' | 'cancelado';
}

export interface UseTallerReturn {
  // Estado
  talleres: Taller[];
  taller: Taller | null;
  loading: boolean;
  error: string | null;

  // Funciones de fetch
  fetchAllTalleres: (incluirInscritos?: boolean) => Promise<Taller[]>;
  fetchTallerById: (id: string) => Promise<Taller | null>;
  fetchInscripciones: (tallerId: string) => Promise<{
    internos: Inscripcion[];
    externos: InscripcionExterna[];
  }>;

  // Funciones de conteo
  contarInscritos: (tallerId: string) => Promise<{ internos: number; externos: number; total: number }>;

  // Funciones de validación
  calcularEstado: (fecha: any, horaInicio?: string, horaTermino?: string, estadoManual?: string) => string;
  verificarCupoDisponible: (tallerId: string, cupoMax?: number) => Promise<boolean>;
  puedeInscribirse: (taller: Taller, perfil?: UserPerfil) => boolean;

  // Funciones de inscripción
  crearInscripcion: (
    tallerId: string,
    perfilId: string,
    tipo: 'afiliado' | 'asociado',
    datosExtra?: {
      nombre?: string;
      correo?: string;
      telefono?: string;
      creditos?: number;
      tipoCosto?: 'gratuito' | 'pago';
      comprobantePagoUrl?: string;
      comprobanteNombre?: string;
    }
  ) => Promise<{ success: boolean; codigoQR?: string; error?: string }>;

  // Funciones de eliminación de inscripción
  eliminarInscripcion: (
    inscripcionId: string,
    esExterno: boolean
  ) => Promise<{ success: boolean; error?: string }>;

  // Funciones de asistencia
  registrarAsistencia: (inscripcionId: string, esExterno: boolean) => Promise<boolean>;
  buscarPorQR: (codigoQR: string) => Promise<{
    encontrado: boolean;
    inscripcion?: Inscripcion | InscripcionExterna;
    esExterno?: boolean;
    coleccion?: string;
  }>;

  // Utilidades
  filtrarTalleres: (talleres: Taller[], filtros: TallerFilters) => Taller[];
  formatearFecha: (fecha: any) => string;
  formatearCosto: (taller: Taller) => string;
}

// ============ HOOK PRINCIPAL ============

export function useTaller(): UseTallerReturn {
  const [talleres, setTalleres] = useState<Taller[]>([]);
  const [taller, setTaller] = useState<Taller | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Calcula el estado de un taller basándose en las fechas y horas.
   * Delega a calcularEstadoTaller (fuente única de verdad).
   */
  const calcularEstado = useCallback((
    fecha: any,
    horaInicio?: string,
    horaTermino?: string,
    estadoManual?: string
  ): string => {
    if (estadoManual === 'cancelado') return 'cancelado';

    const fechaDate = fecha?.toDate ? fecha.toDate() : (fecha ? new Date(fecha) : null);
    if (!fechaDate) return 'sin_fecha';

    return calcularEstadoTaller(
      fechaDate,
      horaInicio || '00:00',
      horaTermino || '23:59',
      estadoManual
    );
  }, []);

  /**
   * Obtiene todos los talleres de Firestore.
   * Si incluirInscritos=true, hace solo 2 queries adicionales (batch)
   * en lugar de 2 por cada taller (N+1).
   */
  const fetchAllTalleres = useCallback(async (incluirInscritos = false): Promise<Taller[]> => {
    setLoading(true);
    setError(null);

    try {
      const snap = await getDocs(collection(db, 'taller'));

      // Conteo batch: 2 queries totales en lugar de 2*N
      let conteoMap: Record<string, number> = {};
      if (incluirInscritos) {
        const [internosSnap, externosSnap] = await Promise.all([
          getDocs(collection(db, 'inscripcion')),
          getDocs(collection(db, 'externo_inscripcion'))
        ]);

        internosSnap.docs.forEach(d => {
          const tid = d.data().taller_doc_id;
          if (tid) conteoMap[tid] = (conteoMap[tid] || 0) + 1;
        });
        externosSnap.docs.forEach(d => {
          const tid = d.data().taller_doc_id;
          if (tid) conteoMap[tid] = (conteoMap[tid] || 0) + 1;
        });
      }

      const talleresData = snap.docs.map((tallerDoc) => {
        const data = tallerDoc.data();
        const fechaBase = data.fecha || data.fechaInicio;

        return {
          id: tallerDoc.id,
          ...data,
          estadoCalculado: calcularEstado(
            fechaBase,
            data.horaInicio,
            data.horaTermino,
            data.estado
          ),
          inscritos: incluirInscritos ? (conteoMap[tallerDoc.id] || 0) : 0
        } as Taller;
      });

      // Ordenar por fecha (más recientes primero)
      talleresData.sort((a, b) => {
        const fechaA = a.fecha?.toDate?.() || a.fechaInicio?.toDate?.() || new Date(0);
        const fechaB = b.fecha?.toDate?.() || b.fechaInicio?.toDate?.() || new Date(0);
        return fechaB.getTime() - fechaA.getTime();
      });

      setTalleres(talleresData);
      return talleresData;
    } catch (err: any) {
      const mensaje = 'Error al cargar talleres';
      setError(mensaje);
      console.error(mensaje, err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [calcularEstado]);

  /**
   * Obtiene un taller específico por ID
   */
  const fetchTallerById = useCallback(async (id: string): Promise<Taller | null> => {
    setLoading(true);
    setError(null);

    try {
      const docRef = doc(db, 'taller', id);
      const snap = await getDoc(docRef);

      if (!snap.exists()) {
        setTaller(null);
        return null;
      }

      const data = snap.data();
      const fechaBase = data.fecha || data.fechaInicio;

      const tallerData: Taller = {
        id: snap.id,
        ...data,
        estadoCalculado: calcularEstado(fechaBase, data.horaInicio, data.horaTermino, data.estado)
      } as Taller;

      setTaller(tallerData);
      return tallerData;
    } catch (err: any) {
      const mensaje = 'Error al cargar el taller';
      setError(mensaje);
      console.error(mensaje, err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [calcularEstado]);

  /**
   * Cuenta inscripciones de un taller (internos y externos)
   */
  const contarInscritos = useCallback(async (tallerId: string): Promise<{
    internos: number;
    externos: number;
    total: number;
  }> => {
    try {
      // Contar inscripciones internas
      const qInternos = query(
        collection(db, 'inscripcion'),
        where('taller_doc_id', '==', tallerId)
      );
      const internosSnap = await getDocs(qInternos);

      // Contar inscripciones externas
      const qExternos = query(
        collection(db, 'externo_inscripcion'),
        where('taller_doc_id', '==', tallerId)
      );
      const externosSnap = await getDocs(qExternos);

      return {
        internos: internosSnap.size,
        externos: externosSnap.size,
        total: internosSnap.size + externosSnap.size
      };
    } catch (err) {
      console.error('Error contando inscritos:', err);
      return { internos: 0, externos: 0, total: 0 };
    }
  }, []);

  /**
   * Obtiene las inscripciones de un taller
   */
  const fetchInscripciones = useCallback(async (tallerId: string): Promise<{
    internos: Inscripcion[];
    externos: InscripcionExterna[];
  }> => {
    try {
      // Obtener inscripciones internas
      const qInternos = query(
        collection(db, 'inscripcion'),
        where('taller_doc_id', '==', tallerId)
      );
      const internosSnap = await getDocs(qInternos);
      const internos = internosSnap.docs.map(d => ({
        id: d.id,
        ...d.data()
      })) as Inscripcion[];

      // Obtener inscripciones externas
      const qExternos = query(
        collection(db, 'externo_inscripcion'),
        where('taller_doc_id', '==', tallerId)
      );
      const externosSnap = await getDocs(qExternos);
      const externos = externosSnap.docs.map(d => ({
        id: d.id,
        ...d.data()
      })) as InscripcionExterna[];

      return { internos, externos };
    } catch (err) {
      console.error('Error obteniendo inscripciones:', err);
      return { internos: [], externos: [] };
    }
  }, []);

  /**
   * Verifica si hay cupo disponible en un taller
   */
  const verificarCupoDisponible = useCallback(async (
    tallerId: string,
    cupoMax?: number
  ): Promise<boolean> => {
    if (!cupoMax) return true; // Sin límite de cupo

    const { total } = await contarInscritos(tallerId);
    return total < cupoMax;
  }, [contarInscritos]);

  /**
   * Verifica si un usuario puede inscribirse a un taller
   */
  const puedeInscribirse = useCallback((
    taller: Taller,
    perfil?: UserPerfil
  ): boolean => {
    const objetivo = taller.publicoObjetivo;

    if (!objetivo || objetivo === 'abierto_publico') {
      return true;
    }

    if (!perfil) return false;

    // Normaliza: colección `asesor` == rol "afiliado"
    const esAfiliado = perfil.tipo === 'afiliado' || (perfil.tipo as string) === 'asesor';
    const esAsociado = perfil.tipo === 'asociado';

    switch (objetivo) {
      case 'afiliados':
        return esAfiliado;
      case 'asociados':
        return esAsociado;
      case 'afiliados_asociados':
        return esAfiliado || esAsociado;
      default:
        return true;
    }
  }, []);

  /**
   * Crea una nueva inscripción usando una transacción para evitar
   * race conditions que podrían exceder el cupo máximo.
   */
  const crearInscripcion = useCallback(async (
    tallerId: string,
    perfilId: string,
    tipo: 'afiliado' | 'asociado',
    datosExtra?: {
      nombre?: string;
      correo?: string;
      telefono?: string;
      creditos?: number;
      tipoCosto?: 'gratuito' | 'pago';
      comprobantePagoUrl?: string;
      comprobanteNombre?: string;
    }
  ): Promise<{ success: boolean; codigoQR?: string; error?: string }> => {
    try {
      const codigoQR = uuidv4();

      await runTransaction(db, async (transaction) => {
        // 1. Leer el taller dentro de la transacción para verificar cupo
        const tallerRef = doc(db, 'taller', tallerId);
        const tallerSnap = await transaction.get(tallerRef);

        if (!tallerSnap.exists()) {
          throw new Error('El taller no existe');
        }

        const tallerData = tallerSnap.data();
        const cupoMaximo = tallerData.cupoMaximo;

        // 2. Verificar si el usuario ya está inscrito
        const qExistente = query(
          collection(db, 'inscripcion'),
          where('taller_doc_id', '==', tallerId),
          where('perfil_id', '==', perfilId)
        );
        const existenteSnap = await getDocs(qExistente);
        if (!existenteSnap.empty) {
          throw new Error('Ya estás inscrito en este taller');
        }

        // 3. Verificar cupo disponible si hay límite
        if (cupoMaximo) {
          const [internosSnap, externosSnap] = await Promise.all([
            getDocs(query(collection(db, 'inscripcion'), where('taller_doc_id', '==', tallerId))),
            getDocs(query(collection(db, 'externo_inscripcion'), where('taller_doc_id', '==', tallerId)))
          ]);
          const totalInscritos = internosSnap.size + externosSnap.size;

          if (totalInscritos >= cupoMaximo) {
            throw new Error('No hay cupo disponible en este taller');
          }
        }

        // 4. Crear la inscripción dentro de la transacción
        const inscripcionData: any = {
          taller_doc_id: tallerId,
          perfil_id: perfilId,
          tipo_perfil: tipo,
          codigo_qr: codigoQR,
          fechaInscripcion: Timestamp.now(),
          asistencia: false,
          completado: false,
          estatus_pago: datosExtra?.tipoCosto === 'gratuito' ? 'completado' : 'pendiente',
          creditos: datosExtra?.creditos || 0
        };

        if (datosExtra) {
          if (datosExtra.nombre) inscripcionData.nombre = datosExtra.nombre;
          if (datosExtra.correo) inscripcionData.correo = datosExtra.correo;
          if (datosExtra.telefono) inscripcionData.telefono = datosExtra.telefono;
          if (datosExtra.comprobantePagoUrl) {
            inscripcionData.comprobantePagoUrl = datosExtra.comprobantePagoUrl;
            inscripcionData.comprobanteNombre = datosExtra.comprobanteNombre || '';
            inscripcionData.comprobanteFecha = Timestamp.now();
          }
        }

        const nuevaRef = doc(collection(db, 'inscripcion'));
        transaction.set(nuevaRef, inscripcionData);

        return { success: true };
      });

      return { success: true, codigoQR };
    } catch (err: any) {
      console.error('Error creando inscripción:', err);
      return { success: false, error: err.message };
    }
  }, []);

  /**
   * Elimina una inscripción (interna o externa) de un taller.
   * El cupo se libera automáticamente porque los conteos son dinámicos.
   * El QR del inscrito quedará inválido (escanearlo dirá "QR no encontrado").
   */
  const eliminarInscripcion = useCallback(async (
    inscripcionId: string,
    esExterno: boolean
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const coleccion = esExterno ? 'externo_inscripcion' : 'inscripcion';
      await deleteDoc(doc(db, coleccion, inscripcionId));
      return { success: true };
    } catch (err: any) {
      console.error('Error eliminando inscripción:', err);
      return { success: false, error: err?.message || 'Error desconocido' };
    }
  }, []);

  /**
   * Busca una inscripción por código QR
   */
  const buscarPorQR = useCallback(async (codigoQR: string): Promise<{
    encontrado: boolean;
    inscripcion?: Inscripcion | InscripcionExterna;
    esExterno?: boolean;
    coleccion?: string;
  }> => {
    try {
      // Buscar en inscripciones internas
      const qInternos = query(
        collection(db, 'inscripcion'),
        where('codigo_qr', '==', codigoQR)
      );
      const internosSnap = await getDocs(qInternos);

      if (!internosSnap.empty) {
        const docData = internosSnap.docs[0];
        return {
          encontrado: true,
          inscripcion: { id: docData.id, ...docData.data() } as Inscripcion,
          esExterno: false,
          coleccion: 'inscripcion'
        };
      }

      // Buscar en inscripciones externas
      const qExternos = query(
        collection(db, 'externo_inscripcion'),
        where('codigo_qr', '==', codigoQR)
      );
      const externosSnap = await getDocs(qExternos);

      if (!externosSnap.empty) {
        const docData = externosSnap.docs[0];
        return {
          encontrado: true,
          inscripcion: { id: docData.id, ...docData.data() } as InscripcionExterna,
          esExterno: true,
          coleccion: 'externo_inscripcion'
        };
      }

      return { encontrado: false };
    } catch (err) {
      console.error('Error buscando por QR:', err);
      return { encontrado: false };
    }
  }, []);

  /**
   * Registra la asistencia de un inscrito.
   *
   * `completado` solo queda en true si el taller es gratuito o si el pago
   * ya estaba marcado como completado. En eventos de pago con pago pendiente,
   * la asistencia se registra pero `completado` queda en false hasta validar el pago.
   * `estatus_pago` solo se fuerza a 'completado' cuando el taller es gratuito.
   */
  const registrarAsistencia = useCallback(async (
    inscripcionId: string,
    esExterno: boolean
  ): Promise<boolean> => {
    try {
      const coleccion = esExterno ? 'externo_inscripcion' : 'inscripcion';
      const docRef = doc(db, coleccion, inscripcionId);

      const inscSnap = await getDoc(docRef);
      if (!inscSnap.exists()) return false;
      const inscData = inscSnap.data();

      let esGratuito = false;
      const tallerRefId = inscData.taller_doc_id || inscData.taller_id;
      if (tallerRefId) {
        const tallerSnap = await getDoc(doc(db, 'taller', tallerRefId));
        if (tallerSnap.exists()) {
          esGratuito = tallerSnap.data().tipoCosto === 'gratuito';
        }
      }

      const pagoCompletado = inscData.estatus_pago === 'completado';
      const completado = esGratuito || pagoCompletado;

      await updateDoc(docRef, {
        asistencia: true,
        completado,
        ...(esGratuito ? { estatus_pago: 'completado' } : {}),
        fecha_asistencia: Timestamp.now()
      });

      return true;
    } catch (err) {
      console.error('Error registrando asistencia:', err);
      return false;
    }
  }, []);

  /**
   * Filtra talleres según criterios
   */
  const filtrarTalleres = useCallback((
    talleresList: Taller[],
    filtros: TallerFilters
  ): Taller[] => {
    return talleresList.filter(t => {
      // Filtro por nombre
      if (filtros.nombre && !t.nombre?.toLowerCase().includes(filtros.nombre.toLowerCase())) {
        return false;
      }

      // Filtro por mes
      if (filtros.mes) {
        const fecha = t.fecha?.toDate?.() || t.fechaInicio?.toDate?.();
        if (!fecha) return false;
        const mesAnio = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
        if (mesAnio !== filtros.mes) return false;
      }

      // Filtro por costo
      if (filtros.costo && filtros.costo !== 'todos') {
        if (t.tipoCosto !== filtros.costo) return false;
      }

      // Filtro por créditos
      if (filtros.creditos && filtros.creditos !== 'todos') {
        const tieneCreditos = (t.creditos || 0) > 0;
        if (filtros.creditos === 'con_creditos' && !tieneCreditos) return false;
        if (filtros.creditos === 'sin_creditos' && tieneCreditos) return false;
      }

      // Filtro por estado
      if (filtros.estado && filtros.estado !== 'todos') {
        if (t.estadoCalculado !== filtros.estado) return false;
      }

      return true;
    });
  }, []);

  /**
   * Formatea una fecha de Firestore para mostrar
   */
  const formatearFecha = useCallback((fecha: any): string => {
    if (!fecha) return 'Sin fecha';

    const date = fecha?.toDate ? fecha.toDate() : new Date(fecha);
    return date.toLocaleDateString('es-MX', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }, []);

  /**
   * Formatea el costo de un taller para mostrar
   */
  const formatearCosto = useCallback((taller: Taller): string => {
    if (taller.tipoCosto === 'gratuito') {
      return 'Gratuito';
    }

    if (taller.tipoCosto === 'pago' && taller.precio) {
      const moneda = taller.moneda || 'MXN';
      return `${moneda} $${taller.precio.toLocaleString()}`;
    }

    return 'Por definir';
  }, []);

  return {
    // Estado
    talleres,
    taller,
    loading,
    error,

    // Funciones de fetch
    fetchAllTalleres,
    fetchTallerById,
    fetchInscripciones,

    // Funciones de conteo
    contarInscritos,

    // Funciones de validación
    calcularEstado,
    verificarCupoDisponible,
    puedeInscribirse,

    // Funciones de inscripción
    crearInscripcion,
    eliminarInscripcion,

    // Funciones de asistencia
    registrarAsistencia,
    buscarPorQR,

    // Utilidades
    filtrarTalleres,
    formatearFecha,
    formatearCosto
  };
}

export default useTaller;
